"use client"

import { useState, useEffect } from "react"
import { Modal, Button } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { createLeadOtherinfoAction } from "../../../../../store/actions/AddleadotherinformationAction"

const OtherPersonalModal = ({ isOpen, onClose, formData }) => {
  const dispatch = useDispatch()
  const leadDetails = useSelector((state) => state.leadState?.leadDetails)
  const [formState, setFormState] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    contact: "",
    email: "",
    gender: "",
    date_of_birth: "",
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen && formData) {
      setFormState({
        first_name: formData.first_name || "",
        middile_name: formData.middle_name || "",
        last_name: formData.last_name || "",
        contact: formData.contact || formData.Contact || "", // Handle both 'contact' and 'Contact'
        email: formData.email || "",
        gender: formData.gender || "",
        date_of_birth: formData.date_of_birth || formData.dob || "", // Handle both 'date_of_birth' and 'dob'
      })
    }
  }, [isOpen, formData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formState.first_name?.trim()) {
      newErrors.first_name = "First name is required"
    }
    if (!formState.last_name?.trim()) {
      newErrors.last_name = "Last name is required"
    }
    if (!formState.contact?.trim()) {
      newErrors.contact = "Contact number is required"
    } else if (!/^[0-9]{10}$/.test(formState.contact.replace(/\D/g, ""))) {
      newErrors.contact = "Please enter a valid 10-digit phone number"
    }
    if (formState.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Please enter a valid email address"
    }
    if (!formState.gender) {
      newErrors.gender = "Gender is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    const leadId = leadDetails?.lead_id
    if (!leadId) {
      setErrors((prev) => ({ ...prev, lead_id: "Lead ID is missing. Cannot submit." }))
      return
    }
    const payload = {
      lead_id: leadId,
      first_name: formState.first_name,
      middile_name: formState.middle_name,
      last_name: formState.last_name,
      lead_for: "Other",
      email: formState.email,
      whatsapp_contact_no: formState.contact,
      gender: formState.gender,
      date_of_birth: formState.date_of_birth, // This will be in YYYY-MM-DD from the date input
    }
    try {
      await dispatch(createLeadOtherinfoAction(payload))
      handleClose()
    } catch (error) {
      console.error("Failed to submit other personal info:", error)
    }
  }

  const handleClose = () => {
    setErrors({})
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Other Person Information</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <form onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <div className="mb-4">
            <h6 className="text-primary mb-3">Personal Information</h6>
            {/* Name Fields Row */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  First Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
                  value={formState.first_name}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  style={{ height: "45px" }}
                />
                {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Middle Name</label>
                <input
                  type="text"
                  name="middle_name"
                  className="form-control"
                  value={formState.middle_name}
                  onChange={handleChange}
                  placeholder="Enter middle name"
                  style={{ height: "45px" }}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">
                  Last Name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className={`form-control ${errors.last_name ? "is-invalid" : ""}`}
                  value={formState.last_name}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  style={{ height: "45px" }}
                />
                {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
              </div>
            </div>
            {/* Contact and Email Row */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Contact Number <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  name="contact"
                  className={`form-control ${errors.contact ? "is-invalid" : ""}`}
                  value={formState.contact}
                  onChange={handleChange}
                  placeholder="Enter contact number"
                  maxLength="10"
                  style={{ height: "45px" }}
                />
                {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  value={formState.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  style={{ height: "45px" }}
                />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>
            </div>
            {/* Gender and Relationship Row */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  Gender <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  name="gender"
                  className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                  value={formState.gender}
                  onChange={handleChange}
                  style={{ height: "45px" }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Transgender">Transgender</option>
                </select>
                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Date of Birth</label>
                <input
                  type="date"
                  name="date_of_birth"
                  className="form-control"
                  value={formState.date_of_birth}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]} 
                  style={{ height: "45px" }}
                />
              </div>
            </div>
          </div>
          {/* Other Details Section */}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Save Information
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default OtherPersonalModal;
